'use client';

import { setToken } from '@/helpers/apiRequestHelper';
import { signIn } from '@/helpers/authHelper';
import { usePageLoader } from '@/hooks/usePageLoader';
import { useSnackbar } from '@/hooks/useSnackbar';
import { ibowClient } from '@/init/ibowClient';
import { pathInfo } from '@/types/pathInfo';
import { PORTAL_USE_TYPE } from '@/types/types';
import { signOut } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

type LogicProps = {
  proxyToken?: string;
  isAdmin?: boolean;
};

export const useLogic = ({ proxyToken, isAdmin }: LogicProps) => {
  const { setLoading } = usePageLoader();
  const { openSnackbar } = useSnackbar();
  const searchParams = useSearchParams();
  const useType = parseInt(
    searchParams.get(pathInfo['/signin'].searchParams.use_type) || '',
  );
  const errorMessage = isAdmin
    ? '管理者ログインに失敗しました。'
    : '代理ログインに失敗しました。';
  const redirectUrl = searchParams.get(
    pathInfo['/signin'].searchParams.redirect,
  );

  useEffect(() => {
    if (!proxyToken) return;
    if (!!proxyToken && !useType) {
      openSnackbar(errorMessage, 'error');
      return;
    }
    setLoading(true);
    signOut({ redirect: false });
    if (useType === PORTAL_USE_TYPE.PORTAL_STAFF) {
      ibowClient.authentication
        .postPortalStaffOneTimeLogin({
          body: { onetime_token: proxyToken, is_admin_login: isAdmin },
        })
        .then(async (res) => {
          await setToken({ token: res.token, refToken: res.ref_token });

          if (redirectUrl) {
            await signIn({ redirect: true, callbackUrl: redirectUrl });
            return;
          }

          await signIn({ redirect: true, callbackUrl: '/admin' });
        })
        .catch(() => {
          openSnackbar(errorMessage, 'error');
          setLoading(false);
        });
    } else if (useType === PORTAL_USE_TYPE.REQUEST_USER) {
      ibowClient.authentication
        .postRequestUserOneTimeLogin({
          body: { onetime_token: proxyToken, is_admin_login: isAdmin },
        })
        .then(async (res) => {
          await setToken({ token: res.token, refToken: res.ref_token });

          if (redirectUrl) {
            await signIn({ redirect: true, callbackUrl: redirectUrl });
            return;
          }

          await signIn({ redirect: true, callbackUrl: '/app' });
        })
        .catch(() => {
          openSnackbar(errorMessage, 'error');
          setLoading(false);
        });
    } else {
      openSnackbar(errorMessage, 'error');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proxyToken, useType]);
};
